import type { JSX } from 'preact';
import { t } from '../i18n.js';
import { IconFilter } from './icons.js';
import { reloadAlbumItems } from '../actions.js';
import { MAX_THRESHOLD } from '../visual-duplicates.js';
import {
  albumItems,
  albums,
  albumsInScope,
  applicableFilter,
  cameras,
  duplicateThreshold,
  duplicates,
  duplicatesAvailable,
  duplicatesMode,
  emojiPalette,
  entities,
  galleryFilter,
  gallerySort,
  items,
  me,
  ownersInScope,
  searchQuery,
  searching,
  setDuplicateThreshold,
  setGallerySort,
  view,
  type GallerySort,
} from '../state.js';

/**
 * **Les filtres de la photothèque — et leur limite, écrite dans l'écran.**
 *
 * Ils sont **clients**, par décision : `vision.md` §6.2 écarte volontairement
 * `Camera`, l'auteur et les emoji des critères serveur. Aucun index ne les porte,
 * et en fabriquer demanderait un GSI par critère pour un corpus qui tient dans le
 * miroir.
 *
 * La conséquence n'est pas cachée : **ils portent sur ce qui est synchronisé**, et
 * la phrase est affichée. Sans elle, un filtre qui ne rend rien se lit comme « je
 * n'ai pas cette photo » alors qu'il faut lire « elle n'est pas encore ici » —
 * c'est la même règle que pour la recherche (C6), et le même piège.
 *
 * **Elle a rejoint le tiroir** *(2026-08-15)*. Elle occupait une rangée sous le
 * bouton, en permanence, sur tous les écrans : « le texte "les filtres…" est
 * inutile ». Il l'est là où il était — on n'y pose pas de filtre —, et il ne
 * l'est pas là où on en pose un : la phrase se lit désormais **dans** le tiroir
 * ouvert, à côté des champs qu'elle qualifie.
 *
 * Le compte, lui, monte **sur la ligne du bouton** et n'y paraît que sous filtre.
 * « 7 sur 7 » écrit en permanence est une ligne qui ne dit rien ; « 1 sur 31 » à
 * côté du mot *Filtres* dit tout. Les deux nombres restent lisibles par la
 * machine en toutes circonstances — `data-shown` et `data-total` sur la section —
 * parce qu'un parcours doit pouvoir constater la taille du corpus sans qu'on
 * l'écrive à l'écran.
 */
export function Filters({
  /**
   * Ce qui **partage la ligne du bouton**, à droite. Une collection y met sa
   * bascule d'affichage : elle n'a pas de rangée de commandes où se poser, et
   * deux boutons sur deux rangées, c'est une rangée de trop *(2026-08-15)*.
   */
  trailing,
}: {
  readonly trailing?: JSX.Element | undefined;
} = {}): JSX.Element {
  const f = galleryFilter.value;
  // Le total porte sur **ce que l'écran montrerait sans filtre**, et l'écran
  // dépend de l'axe : dans un dossier, c'est ce qui y est rangé ; sur l'axe
  // *Tout*, ce sont tous les fichiers, collections exclues. Un total global dans
  // un dossier de trois photos aurait affiché « 3 sur 412 », ce qui ne répond à
  // aucune question qu'on se pose en l'ouvrant.
  const v = view.value;
  // **Le périmètre de l'axe, avant filtres.** Il sert deux fois : au total affiché,
  // et aux listes de choix de R3 — qui ne doivent proposer que des réponses non
  // vides d'avance, et ne doivent pas rétrécir pendant qu'on s'en sert.
  //
  // Une collection ne se compte pas dans le miroir : son contenu vient d'une
  // route, pas de `/changes` — filtrer les entités par `Folder` y donnerait zéro.
  const scope =
    v.kind === 'collection'
      ? albumItems.value
      : entities.value.filter((e) =>
          v.kind === 'folder' ? e.Folder === v.folder : e.Aid === undefined,
        );
  const total = scope.length;
  const shown = items.value.length;
  // Le **nombre** de critères, pas seulement leur présence : c'est ce que porte
  // la pastille du résumé, et c'est la seule chose qu'un repli doit laisser voir.
  // *La nature compte pour un* — elle s'écrit sur deux champs du modèle, mais
  // elle se pose d'un seul geste ; en compter deux pour « Photos » dirait à
  // l'écran qu'on a posé un filtre de plus qu'on ne croit.
  //
  // **Il compte ce que la grille applique**, et c'est `applicableFilter` qui le dit
  // *(R3)* : un critère hérité d'un autre écran n'a rien à faire dans la pastille
  // de celui-ci — elle annoncerait un filtre posé pendant que la grille montre
  // tout.
  const shownFilter = applicableFilter(f, v.kind);
  const count =
    (shownFilter.type !== undefined || shownFilter.kind !== undefined ? 1 : 0) +
    (shownFilter.minScore !== undefined ? 1 : 0) +
    (shownFilter.camera !== undefined ? 1 : 0) +
    (shownFilter.emoji !== undefined ? 1 : 0) +
    (shownFilter.album !== undefined ? 1 : 0) +
    (shownFilter.owner !== undefined ? 1 : 0) +
    (shownFilter.duplicates ? 1 : 0);
  // **Une recherche montre aussi le compte** *(R7)* : « 3 sur 212 » est la
  // première chose qu'on veut savoir d'une réponse. Elle ne gonfle pas la pastille
  // pour autant — la saisie est à l'écran, pas repliée dans le tiroir.
  const active = count > 0 || searching.value;

  // Les deux champs de R3 sont **exclusifs l'un de l'autre par construction** :
  // l'un ne vit que dans un dossier ou sur l'axe *Tout*, l'autre que dans un
  // partage. Il n'y a donc jamais six champs à l'écran, toujours cinq.
  const inCollection = v.kind === 'collection';
  // **Où les doublons sont offerts se demande à la règle** *(DB12)*, jamais à une
  // liste d'écrans recopiée ici : c'est ce qui a manqué le jour où le filtre s'est
  // ouvert aux partages sans que la case y paraisse.
  const duplicatable = duplicatesAvailable(v.kind);
  const shareChoices = inCollection ? [] : albumsInScope(scope, albums.value);
  const ownerChoices = inCollection
    ? ownersInScope(albumItems.value, me.value?.uid, {
        mine: t('gallery.ownerMe'),
        anonymous: t('gallery.ownerUnknown'),
      })
    : [];

  const set = (patch: Partial<typeof f>): void => {
    galleryFilter.value = prune({ ...f, ...patch });
  };

  return (
    <section
      class="filters"
      data-testid="filters"
      aria-label={t('gallery.filters')}
      data-shown={shown}
      data-total={total}
    >
      {/**
       * **Quatre champs avant la première photo, et une palette d'emoji dessous.**
       * *(Cinq jusqu'au 2026-08-09, où type et nature n'en font plus qu'un.)*
       *
       * Ils étaient dépliés d'emblée au-delà de 52 rem : « sur le web PC les
       * filtres sont toujours dépliés, ça prend de la place pour pas grand
       * chose ». C'est exact, et la largeur n'y changeait rien — un filtre est un
       * geste **occasionnel**, et un contrôle occasionnel qui occupe en permanence
       * deux rangs au-dessus du contenu se paie sur tous les autres jours. La
       * capture du 2026-08-09 le montre : cinq listes et onze emoji entre le titre
       * et les deux photos du dossier.
       *
       * Ils se replient donc **partout**, et le repli n'est plus une affaire de
       * largeur — c'est un `<details>` fermé, ce qui supprime au passage le seul
       * endroit du produit où un attribut dépendait d'un signal de largeur.
       *
       * Ce qui reste visible est **ce qui est actif** : le résumé porte le nombre
       * de critères posés, et la ligne de compte reste hors du repli. Un filtre
       * qu'on a oublié d'enlever ne doit jamais pouvoir se cacher.
       */}
      {/* **La recherche, hors du tiroir** *(R7, 2026-09-13)*. Un filtre est un
          geste occasionnel, et se replie ; chercher est le premier geste qu'on fait
          dans une photothèque de mille images, et un champ qu'il faut d'abord
          déplier est un champ qu'on ne trouve pas. */}
      <div class="search-bar">
        <input
          type="search"
          class="input search-input"
          data-testid="search"
          aria-label={t('search.label')}
          placeholder={t('search.placeholder')}
          value={searchQuery.value}
          onInput={(e) => (searchQuery.value = (e.currentTarget as HTMLInputElement).value)}
        />
      </div>
      {/* **La limite se lit pendant qu'on cherche** (C6), et seulement alors : elle
          qualifie une réponse, et une phrase permanente au-dessus de la grille est
          une phrase qu'on cesse de lire. */}
      {searching.value ? (
        <p class="search-note" data-testid="search-note">
          {t('search.limit')}
        </p>
      ) : null}
      <div class="filter-bar">
      <details class="filter-fold">
        <summary class="filter-summary">
          <IconFilter size={16} />
          <span>{t('gallery.filters')}</span>
          {count > 0 ? <span class="pill accent">{count}</span> : null}
          {/* **Le compte est dans le résumé**, donc toujours visible, y compris
              tiroir fermé — un filtre qu'on a oublié d'enlever ne doit jamais
              pouvoir se cacher. Il n'apparaît qu'**actif** : sans filtre, les
              deux nombres sont le même, et « 7 sur 7 » ne répond à aucune
              question. */}
          {active ? (
            <span class="filter-scope" data-testid="filter-scope">
              {t('gallery.count', { shown, total })}
            </span>
          ) : null}
        </summary>
        <div class="filter-row">
        {/* **Le filtre par type remplace les cinq onglets de la V1** sans les
            faire revenir : un filtre se cumule avec les autres et se retire d'un
            geste, là où un onglet excluait tout le reste.

            **Un seul champ, alors que le modèle en a deux** *(2026-08-09)*.
            `Type` et `Kind` étaient offerts côte à côte, et le retour d'usage l'a
            dit : « regrouper les filtres type et nature, ils n'ont pas de sens
            indépendamment et consomment 2 emplacements ». C'est exact deux fois.
            `Kind` **ne vit que sous `MEDIA`** (`data-model.md` : `IMG | VID` pour
            `MEDIA`), si bien que *Nature = Photos* sans *Type = Photos et vidéos*
            ne disait rien de plus, et que *Type = Notes* avec *Nature = Vidéos*
            était un couple vide qu'on pouvait poser — un filtre qui ne rend
            jamais rien et n'explique pas pourquoi. La liste est donc **plate et
            close** : ce sont les quatre natures qu'on peut ranger dans un espace,
            et chacune s'écrit sur les deux champs d'un coup. */}
        <label class="field">
          <span>{t('gallery.type')}</span>
          <select
            data-testid="filter-type"
            value={natureCode(f)}
            onChange={(e) => set(natureFilter((e.currentTarget as HTMLSelectElement).value))}
          >
            <option value="">{t('gallery.any')}</option>
            <option value="IMG">{t('gallery.images')}</option>
            <option value="VID">{t('gallery.videos')}</option>
            <option value="NOTE">{t('gallery.notes')}</option>
            <option value="DOC">{t('gallery.docs')}</option>
          </select>
        </label>

        <label class="field">
          {/* « Au moins », jamais « exactement » : une note est un seuil de
              qualité, et personne ne cherche ses photos notées 3 sur 5 pile. */}
          <span>{t('gallery.minScore')}</span>
          <select
            data-testid="filter-score"
            value={f.minScore === undefined ? '' : String(f.minScore)}
            onChange={(e) => {
              const raw = (e.currentTarget as HTMLSelectElement).value;
              set({ minScore: raw ? Number(raw) : undefined });
            }}
          >
            <option value="">{t('gallery.any')}</option>
            {/* Un `<option>` ne peut pas porter d'icône : c'est le seul endroit où
                le texte reste seul, et « 3 et plus » y dit mieux que trois étoiles
                empilées ce que le filtre fait — un seuil, jamais une égalité. */}
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={String(n)}>
                {t('gallery.atLeast', { count: n })}
              </option>
            ))}
          </select>
        </label>

        <label class="field">
          <span>{t('gallery.camera')}</span>
          <select
            data-testid="filter-camera"
            value={f.camera ?? ''}
            onChange={(e) =>
              set({ camera: (e.currentTarget as HTMLSelectElement).value || undefined })
            }
          >
            {/* Seulement ce qui existe : `Camera` est ce que l'EXIF a écrit, et
                personne ne l'a normalisé — une liste fermée n'aurait aucun sens. */}
            <option value="">{t('gallery.any')}</option>
            {cameras.value.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        {/* **Le partage, dans un dossier** *(R3, 2026-09-12)*.
            « Lorsqu'on est dans un dossier il manque un filtre sur les partages,
            pour lister les éléments partagés sur un partage spécifique ou dans
            aucun partage. »

            La dernière moitié de la phrase est **la moitié qui coûte**, et c'est
            elle qui décide de la forme du champ : *aucun* n'est pas l'absence de
            filtre, c'est un critère — celui qui répond à *qu'est-ce que je n'ai
            pas encore partagé ?*. Il a donc sa propre option, distincte de
            *Tous*, et le modèle porte les trois états (`GalleryFilter.album`).

            La liste ne montre que les partages **présents ici** : la même règle
            que pour l'appareil, resserrée au périmètre de l'axe. Elle disparaît
            entièrement quand rien de ce dossier ne circule — un champ dont la
            seule option utile est « aucun » ne pose aucune question. */}
        {shareChoices.length > 0 ? (
          <label class="field">
            <span>{t('gallery.share')}</span>
            <select
              data-testid="filter-share"
              value={f.album === undefined ? '' : (f.album ?? NONE)}
              onChange={(e) => {
                const raw = (e.currentTarget as HTMLSelectElement).value;
                set({ album: raw === '' ? undefined : raw === NONE ? null : raw });
              }}
            >
              <option value="">{t('gallery.any')}</option>
              {/* **`NONE` et non la chaîne vide** : `<select>` ne connaît que des
                  chaînes, et `''` est déjà pris par *Tous*. Une sentinelle qui ne
                  peut pas être un ULID est la seule façon d'écrire trois états
                  dans un champ qui n'en offre que deux. */}
              <option value={NONE}>{t('gallery.shareNone')}</option>
              {shareChoices.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {/* **Le propriétaire, dans un partage** *(R3, 2026-09-12)*.
            « Sur un album partagé il manque un filtre par propriétaire pour isoler
            facilement ses éléments ou ceux d'un autre utilisateur. »

            Il ne paraît qu'à **deux contributeurs ou plus** : dans un partage dont
            tout vient de la même personne, il n'offrirait qu'une réponse, qui est
            déjà à l'écran. */}
        {ownerChoices.length > 1 ? (
          <label class="field">
            <span>{t('gallery.owner')}</span>
            <select
              data-testid="filter-owner"
              value={f.owner ?? ''}
              onChange={(e) =>
                set({ owner: (e.currentTarget as HTMLSelectElement).value || undefined })
              }
            >
              <option value="">{t('gallery.any')}</option>
              {ownerChoices.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label class="field">
          <span>{t('gallery.sort')}</span>
          <select
            data-testid="filter-sort"
            value={gallerySort.value}
            onChange={(e) =>
              setGallerySort((e.currentTarget as HTMLSelectElement).value as GallerySort)
            }
          >
            {/* **Chaque axe garde son ordre.** « Type puis nom » est celui d'un
                dossier — il met les notes avec les notes —, et il n'a pas de sens
                sur une vue chronologique. Le proposer là aurait offert un ordre
                qui défait ce que l'axe promet. */}
            {v.kind === 'folder' ? <option value="kind">{t('gallery.sortKind')}</option> : null}
            <option value="date-desc">{t('gallery.sortNewest')}</option>
            <option value="date-asc">{t('gallery.sortOldest')}</option>
            <option value="name">{t('gallery.sortName')}</option>
            <option value="size-desc">{t('gallery.sortSize')}</option>
          </select>
        </label>
        </div>

        {/* **Les doublons probables** *(K4, 2026-09-13)* — un **interrupteur**, et
            le seuil qui décide de ce qu'il retient. Désactivé, rien n'est calculé ni
            affiché : le mode était d'abord permanent — badges sur toute la grille —,
            et le retour d'usage a demandé de pouvoir l'éteindre. Activé, la grille
            ne montre que les groupes, regroupés, et la glissière paraît : la régler,
            c'est redéfinir ce qu'est un doublon, et on veut voir la grille changer
            pendant qu'on le fait. 2 par défaut ne retient que les doublons vrais —
            copies WhatsApp comprises ; vers 20 viennent les photos proches. */}
        {duplicatable ? (
          <div class="filter-row duplicates-row">
            {/* **Sa propre ligne, sous les listes** *(retour d'usage, 2026-09-13)* :
                posée au milieu des listes, la case s'alignait mal — une case n'a ni
                intitulé ni hauteur de liste. Ici, la case puis, activée, la glissière
                sur la même ligne. La légende du réglage reste en infobulle. */}
            <label class="duplicates-toggle">
              <input
                type="checkbox"
                data-testid="filter-duplicates"
                checked={f.duplicates === true}
                onChange={(e) => {
                  const on = (e.currentTarget as HTMLInputElement).checked;
                  set({ duplicates: on ? true : undefined });
                  // **Dans un partage, les empreintes arrivent avec la page**
                  // *(DB12)* : elles n'ont pas été demandées au chargement, donc
                  // cocher la case relit le partage. Ailleurs, le miroir les a
                  // déjà, et rien ne part.
                  if (on) reloadAlbumItems();
                }}
              />
              <span>
                {duplicatesMode.value
                  ? t('gallery.duplicatesOn', { count: duplicates.value.size })
                  : t('gallery.duplicates')}
              </span>
            </label>
            {duplicatesMode.value ? (
            <label class="duplicates-threshold" title={t('gallery.duplicateThresholdHint')}>
              <span>{t('gallery.duplicateThreshold', { n: duplicateThreshold.value })}</span>
              <input
                type="range"
                min={0}
                max={MAX_THRESHOLD}
                step={1}
                data-testid="duplicate-threshold"
                aria-label={t('gallery.duplicateThresholdHint')}
                value={duplicateThreshold.value}
                onInput={(e) => setDuplicateThreshold(Number((e.currentTarget as HTMLInputElement).value))}
              />
            </label>
            ) : null}
          </div>
        ) : null}

        <div class="filter-row emoji-row" role="group" aria-label={t('gallery.emoji')}>
        {emojiPalette.value.map((emoji) => (
          <button
            key={emoji}
            type="button"
            class={f.emoji === emoji ? 'emoji-chip on' : 'emoji-chip'}
            data-testid={`filter-emoji-${emoji}`}
            aria-pressed={f.emoji === emoji}
            onClick={() => set({ emoji: f.emoji === emoji ? undefined : emoji })}
          >
            {emoji}
          </button>
        ))}
        {active ? (
          <button
            type="button"
            class="btn quiet"
            data-testid="filter-clear"
            onClick={() => (galleryFilter.value = {})}
          >
            {t('gallery.clearFilters')}
          </button>
        ) : null}
        </div>

        {/* La limite est écrite **là où l'on filtre**, et plus sous le bouton :
            c'est au moment de poser un critère qu'il faut savoir sur quoi il
            porte. */}
        <p class="filter-note" data-testid="filter-note">
          {t('gallery.localScope')}
        </p>
      </details>
      {/* Le compagnon de ligne s'aligne **en haut** : le tiroir ouvert grandit
          sous le résumé, et un bouton centré sur un panneau déplié flotterait au
          milieu des champs. */}
      {trailing}
      </div>
    </section>
  );
}

/**
 * **La nature, telle qu'un humain la nomme** — et sa traduction vers les deux
 * champs du modèle.
 *
 * `Type` répond *quelle sorte d'objet* (`MEDIA`, `NOTE`, `DOC`) et `Kind` *quelle
 * sorte de média* (`IMG`, `VID`) : deux champs parce que le stockage en a besoin,
 * une seule question parce que c'est ainsi qu'on la pose. Le code affiché est
 * donc la **feuille** de cette hiérarchie, jamais son nœud intermédiaire — il n'y
 * a pas d'option « médias », qui ne serait que « photos ou vidéos » écrit moins
 * clairement.
 */
function natureCode(f: { readonly type?: string; readonly kind?: string }): string {
  if (f.type === 'MEDIA') return f.kind ?? '';
  return f.type ?? '';
}

function natureFilter(code: string): { type?: string; kind?: string } {
  if (code === 'IMG' || code === 'VID') return { type: 'MEDIA', kind: code };
  // Les deux champs sont **toujours** réécrits ensemble : ne remettre que `type`
  // laisserait un `Kind = VID` derrière soi, et « Notes » ne rendrait plus rien.
  return { type: code || undefined, kind: undefined };
}

/**
 * La valeur du champ *partage* qui dit **« dans aucun »** *(R3)*.
 *
 * Un `<select>` ne transporte que des chaînes, et la chaîne vide désigne déjà
 * *Tous* : il faut donc une sentinelle pour le troisième état. Elle est choisie
 * hors de l'alphabet d'un ULID — Crockford base32, majuscules et chiffres — de
 * sorte qu'aucun `aid` ne puisse jamais la prendre.
 */
const NONE = 'sans-partage';

/** Retire les clés à `undefined` : `{ kind: undefined }` n'est pas « aucun filtre ». */
function prune<T extends Record<string, unknown>>(value: T): T {
  const out = { ...value };
  for (const key of Object.keys(out)) {
    if (out[key] === undefined) delete out[key];
  }
  return out;
}
